package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.comparison.ComparisonCalendar
import com.evoapps.evoshift.domain.comparison.ComparisonMode
import com.evoapps.evoshift.domain.comparison.DateRange
import com.evoapps.evoshift.domain.comparison.PeriodComparison
import com.evoapps.evoshift.domain.comparison.SalesComparisons
import com.evoapps.evoshift.domain.comparison.StatisticsQuery
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import com.evoapps.evoshift.domain.model.DayResults
import com.evoapps.evoshift.domain.model.FixedCost
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.repository.FinancialRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.number

/**
 * SH-225: the dashboard's comparison chips: the week to date against the same days of last week,
 * and the month to date against the same weekdays a year earlier ([lastYearRecords]: what was
 * read for it, for the caller to keep; the past does not change between two loads).
 */
data class DashboardSalesComparison(
    val weekToDate: PeriodComparison,
    val monthToDate: PeriodComparison,
    val lastYearRange: DateRange,
    val lastYearRecords: List<DailyRecord>
)

/**
 * SH-225: reads what the sales comparisons need, never more: one range query of daily records per
 * period ([recordsIn], at most [MAX_RANGE_DAYS] days, so a comparison costs a month of reads at
 * most), and the ledgers of the months they touch for the daily result (one read per month).
 * Money only: the rules refuse the daily records to anyone without the workplace's finances.
 */
class GetSalesComparisonUseCase(
    private val financialRepository: FinancialRepository,
    /** Who asks: without the workplace's finances nothing is read (the rules would refuse it). */
    private val caller: CallerCapabilities? = null
) {

    /** [workplaceId]'s stored records of [range], in one range query; [SalesComparisonNotAllowedException] without the finances. */
    suspend fun recordsIn(workplaceId: String, range: DateRange): Result<List<DailyRecord>> {
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) return Result.failure(SalesComparisonNotAllowedException())
        if (range.length > MAX_RANGE_DAYS) {
            return Result.failure(IllegalArgumentException("a comparison reads at most $MAX_RANGE_DAYS days, not ${range.length}"))
        }
        return financialRepository.getDailyRecordsInRange(workplaceId, range.start.toString(), range.end.toString())
    }

    /**
     * The stored records [mode] compares [period] with ([ComparisonCalendar.readRange]), each as
     * its daily result counts it: its month's recurring ledger items spread over the month's days
     * (DayResults.resultDay), the ledger of each month read once; [knownLedgers] (by roster id)
     * are not read again.
     */
    suspend fun baseRecords(
        workplaceId: String,
        period: DateRange,
        mode: ComparisonMode,
        firstDayOfWeek: Int,
        knownLedgers: Map<String, List<FixedCost>> = emptyMap()
    ): Result<List<DailyRecord>> = runCatching {
        val range = ComparisonCalendar.readRange(period, mode, firstDayOfWeek)
        val records = recordsIn(workplaceId, range).getOrThrow()
        withLedgers(workplaceId, records, knownLedgers)
    }

    /**
     * SH-228: what the Statistics screen needs for [query]: the period's whole weeks with the week
     * before, and its base ([StatisticsQuery.readRanges]), each run split into range queries of at
     * most [MAX_RANGE_DAYS] days, as their daily results count them (the ledger of each month read
     * once; [knownLedgers] by roster id are not read again).
     */
    suspend fun statisticsRecords(
        workplaceId: String,
        query: StatisticsQuery,
        knownLedgers: Map<String, List<FixedCost>> = emptyMap(),
        /** Only these of [StatisticsQuery.readRanges] (the rest was read before); null: all of them. */
        only: List<DateRange>? = null
    ): Result<List<DailyRecord>> = runCatching {
        val records = (only ?: query.readRanges)
            .flatMap { StatisticsQuery.chunks(it, MAX_RANGE_DAYS) }
            .flatMap { recordsIn(workplaceId, it).getOrThrow() }
            .distinctBy { it.date }
        withLedgers(workplaceId, records, knownLedgers)
    }

    /** [records] as their daily results count them, with the ledgers of their months (a failed read: as stored). */
    private suspend fun withLedgers(workplaceId: String, records: List<DailyRecord>, known: Map<String, List<FixedCost>>): List<DailyRecord> {
        val months = records.map { it.rosterId.ifBlank { rosterOf(workplaceId, it.date) } }.toSet()
        val ledgers = coroutineScope {
            months.map { rosterId ->
                async { rosterId to (known[rosterId] ?: financialRepository.getFixedCosts(rosterId).getOrDefault(emptyList())) }
            }.awaitAll().toMap()
        }
        return records.map { record ->
            val ledger = ledgers[record.rosterId.ifBlank { rosterOf(workplaceId, record.date) }].orEmpty()
            DayResults.resultDay(record, ledger)
        }
    }

    private fun rosterOf(workplaceId: String, date: String): String {
        val day = LocalDate.parse(date)
        return RosterIds.of(workplaceId, day.year, day.month.number)
    }

    /**
     * The dashboard's chips for [month] (any day of it): up to [today] in the current month, the
     * whole month in a past one; null for a month still to come. Two range queries: the month and
     * the week before its last week (the current side and last week), and the same weekdays a year
     * earlier, unless [cachedLastYear] already holds them (for [DashboardSalesComparison.lastYearRange]).
     */
    suspend fun dashboard(
        workplaceId: String,
        month: LocalDate,
        today: LocalDate,
        firstDayOfWeek: Int,
        isHoliday: (LocalDate) -> Boolean = { false },
        cachedLastYear: Pair<DateRange, List<DailyRecord>>? = null
    ): Result<DashboardSalesComparison?> = runCatching {
        val monthRange = DateRange.monthOf(month)
        if (monthRange.start > today || !caller.allows(workplaceId, Capability.VIEW_FINANCES)) return@runCatching null
        val reference = minOf(today, monthRange.end)
        val toDate = DateRange(monthRange.start, reference)
        val weekStart = ComparisonCalendar.weekStart(reference, firstDayOfWeek)
        val current = DateRange(minOf(monthRange.start, weekStart.minus(DatePeriod(days = 7))), reference)
        val lastYearRange = ComparisonCalendar.counterpartRange(toDate, ComparisonMode.LAST_YEAR_WEEKDAY)
        val (recent, lastYear) = coroutineScope {
            val recent = async { recordsIn(workplaceId, current).getOrThrow() }
            val lastYear = async {
                cachedLastYear?.takeIf { it.first == lastYearRange }?.second ?: recordsIn(workplaceId, lastYearRange).getOrThrow()
            }
            recent.await() to lastYear.await()
        }
        val comparisons = SalesComparisons(recent + lastYear, isHoliday)
        DashboardSalesComparison(
            weekToDate = comparisons.weekToDate(reference, firstDayOfWeek),
            monthToDate = comparisons.monthToDate(reference, ComparisonMode.LAST_YEAR_WEEKDAY),
            lastYearRange = lastYearRange,
            lastYearRecords = lastYear
        )
    }

    companion object {
        /** The longest run of days one comparison query reads (a month and the edges of its weeks). */
        const val MAX_RANGE_DAYS = 62
    }
}

/** SH-225: comparisons are money: only the owner and financial managers read them. */
class SalesComparisonNotAllowedException : IllegalStateException("sales comparisons need the workplace's finances")
